import { useNavigate } from "react-router-dom";
import { FormWrapper } from "../../components/form-wrapper";
import { Input, SelectInputBoxOption } from "component-library";
import { useForm } from "react-hook-form";
import { ControlledDatePicker } from "../../components/form/controlled-date-picker";
import { useFormState } from "../../components/context/hooks/useFormState";
import { ControlledSelectInputBox } from "../../components/form/controlled-select-input-box";
import { DateValue, Gender } from "@shared/types";
import { usePraxis } from "../../praxis-config/praxis-context";
import { useTranslation } from "react-i18next";

type Data = {
  firstName: string;
  lastName: string;
  phone: string;
  insurance: string;
  birthday: DateValue;
  profession?: string;
  hobby?: string;
  gender: Gender;
};

export const PersonalInformationPage: React.FC = () => {
  const navigate = useNavigate();
  const { flow } = usePraxis();
  const { t } = useTranslation();

  const { formState: contextFormState, updateFormState } = useFormState();

  const { control, handleSubmit, register, formState } = useForm<Data>({
    defaultValues: {
      firstName: contextFormState.firstName,
      lastName: contextFormState.lastName,
      phone: contextFormState.phone,
      insurance: contextFormState.insurance,
      birthday: {
        day: contextFormState.birthday?.day,
        month: contextFormState.birthday?.month,
        year: contextFormState.birthday?.year,
      },
      profession: contextFormState.profession,
      hobby: contextFormState.hobby,
      gender: contextFormState.gender,
    },
  });

  const onNext = (data: Data) => {
    updateFormState(data);

    if (flow.entry.formState) {
      updateFormState(flow.entry.formState);
    }
    navigate(flow.entry.route);
  };

  return (
    <FormWrapper onSubmit={handleSubmit(onNext)}>
      <div className="text-2xl text-text-primary mb-10">
        {t("pages.personal_information.title")}
      </div>
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-2">
          <Input
            {...register("firstName", {
              required: t(
                "pages.personal_information.first_name.validation.required",
              ),
            })}
            autoComplete="given-name"
            error={formState.errors.firstName?.message}
            className="w-full"
            placeholder={t("pages.personal_information.first_name.placeholder")}
          />
          <Input
            {...register("lastName", {
              required: t(
                "pages.personal_information.last_name.validation.required",
              ),
            })}
            autoComplete="family-name"
            error={formState.errors.lastName?.message}
            className="w-full"
            placeholder={t("pages.personal_information.last_name.placeholder")}
          />
        </div>
        <ControlledDatePicker
          required={t(
            "pages.personal_information.date_of_birth.validation.required",
          )}
          label={t("pages.personal_information.date_of_birth.label")}
          name="birthday"
          control={control}
        />
        <Input
          {...register("insurance", {
            required: t(
              "pages.personal_information.insurance.validation.required",
            ),
          })}
          error={formState.errors.insurance?.message}
          placeholder={t("pages.personal_information.insurance.placeholder")}
        />
        <Input
          {...register("phone", {
            required: t("pages.personal_information.phone.validation.required"),
            pattern: {
              value: /^\+?[0-9\s\-()]{7,20}$/,
              message: t("pages.personal_information.phone.validation.format"),
            },
          })}
          error={formState.errors.phone?.message}
          placeholder={t("pages.personal_information.phone.placeholder")}
        />
        <Input
          {...register("profession")}
          placeholder={t("pages.personal_information.profession.placeholder")}
        />
        <Input
          {...register("hobby")}
          placeholder={t("pages.personal_information.sport_hobby.placeholder")}
        />
        <div className="flex flex-col w-full">
          <div className="text-sm mb-1 font-medium text-gray-700 ml-0.5">
            {t("pages.personal_information.gender.label")}
          </div>
          <ControlledSelectInputBox
            required={t(
              "pages.personal_information.gender.validation.required",
            )}
            name="gender"
            control={control}
            isMultiSelect={false}
          >
            <SelectInputBoxOption value="male">
              {t("pages.personal_information.gender.options.male")}
            </SelectInputBoxOption>
            <SelectInputBoxOption value="female">
              {t("pages.personal_information.gender.options.female")}
            </SelectInputBoxOption>
            <SelectInputBoxOption value="diverse">
              {t("pages.personal_information.gender.options.diverse")}
            </SelectInputBoxOption>
          </ControlledSelectInputBox>
        </div>
      </div>
    </FormWrapper>
  );
};
